import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, EyeOff, Mail, Lock, KeyRound } from 'lucide-react';
import { LoginPageSEO } from '@/components/SEOHead';
import RegisterForm from '@/components/RegisterForm';
import { setAuthToken } from '@/lib/axiosAuth';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const LoginPage = () => {
  const { login, isAuthenticated, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Forgot password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from || '/hesabim';
      navigate(from);
    }
  }, [isAuthenticated, navigate, location]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      }, { withCredentials: true });
      
      // Persist the token for Bearer auth (defense against cookie blocking)
      if (response.data?.token) {
        setAuthToken(response.data.token);
      }
      setUser(response.data);
      toast.success('Giriş başarılı!');
      
      // Rol'e göre yönlendir
      if (response.data.role === 'operator') {
        navigate('/isletmeci');
      } else if (response.data.role === 'admin') {
        navigate('/admin');
      } else {
        navigate(location.state?.from || '/hesabim');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || 'Giriş başarısız');
      } else if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Giriş başarısız');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Lütfen email adresinizi girin');
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Şifre sıfırlama bağlantısı email adresinize gönderildi');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8" data-testid="login-page">
      {/* SEO Head */}
      <LoginPageSEO />
      
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-32 h-32 mx-auto mb-2">
            <img 
              src="/logo.webp" 
              alt="TesisKirala Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <p className="text-slate-600 mt-2">
            Spor tesisi bulmak ve rezervasyon yapmak için giriş yapın
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="tab-login">Giriş Yap</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Kayıt Ol</TabsTrigger>
            </TabsList>
            
            {/* Login Tab */}
            <TabsContent value="login" className="space-y-4 mt-4">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="ornek@email.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="login-password">Şifre</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 h-11"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                </Button>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-emerald-700 hover:text-emerald-800 hover:underline"
                  >
                    Şifremi Unuttum
                  </button>
                </div>
              </form>
            </TabsContent>
            
            {/* Register Tab */}
            <TabsContent value="register" className="mt-4">
              <RegisterForm onSuccess={(userData) => setUser(userData)} />
            </TabsContent>
          </Tabs>
          
          <div className="text-center text-sm text-slate-500">
            <p>
              Giriş yaparak{' '}
              <a href="/kullanim-kosullari" className="text-emerald-700 hover:underline">
                Kullanım Koşulları
              </a>
              {' '}ve{' '}
              <a href="/gizlilik-politikasi" className="text-emerald-700 hover:underline">
                Gizlilik Politikası
              </a>
              'nı kabul etmiş olursunuz.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" />
              Şifremi Unuttum
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <p className="text-sm text-slate-600">
              Email adresinizi girin, şifre sıfırlama bağlantısı göndereceğiz.
            </p>
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email Adresi</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="ornek@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={forgotLoading}
                className="flex-1 bg-emerald-700 hover:bg-emerald-800"
              >
                {forgotLoading ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
